import { Plane, Raycaster, Vector2, Vector3, type Camera } from 'three';
import type { ClothPatch } from '../scene/clothPatch';

// Pointer-driven grab interaction for ClothPatch instances. Works with mouse
// + touch via PointerEvent.
//
// Lifecycle per grab:
//
//   pointerdown on cloth ──►  raycast → hit point + cloth → find closest
//                              particle, build a camera-facing drag plane
//                              through that point, pin the particle, mute
//                              OrbitControls
//
//   pointermove          ──►  raycast against the drag plane only (cheap),
//                              moveGrab(intersect) — the verlet sim drags
//                              neighbors along through the constraint pass
//
//   pointerup / leave    ──►  releaseGrab, restore OrbitControls
//
// Pointer event capture: we attach a capturing-phase pointerdown listener
// so we see the event BEFORE OrbitControls. On hit we stopPropagation() so
// the orbiter doesn't start a camera drag underneath us. On miss we don't
// touch the event — the orbiter takes over normally.
//
// `controlsToggle` is an out-parameter: anything that exposes `.enabled`
// (OrbitControls, MapControls, custom rigs) works.

export interface ClothGrabDeps {
  camera: Camera;
  /** Canvas element — the same one passed to OrbitControls. */
  domElement: HTMLElement;
  /** Anything with a boolean `enabled` field; muted during active grab. */
  controlsToggle?: { enabled: boolean };
}

// A press over the cloth that travels less than this is a TAP → it clicks THROUGH
// to the hero behind (the chair), and the cloth never grabs. Past it, a deliberate
// DRAG grabs the cloth. Matches the rail-drag + click-slop threshold so a press
// that doesn't scrub/select also doesn't grab. (John, 2026-06-29: the cloth was
// grabbing on every pointerdown and stealing chair clicks.)
const GRAB_DRAG_THRESHOLD_PX = 6;

interface ActiveGrab {
  cloth: ClothPatch;
  particle: number;
  /** Plane perpendicular to the camera at the original hit's depth. The
   *  cursor maps to a point on this plane each frame so the drag stays at
   *  consistent depth regardless of where the camera was looking. */
  plane: Plane;
  pointerId: number;
}

/** A cloth hit recorded on pointerdown but NOT yet grabbed — promoted to an
 *  ActiveGrab only once the pointer drags past the threshold. */
interface PendingGrab {
  cloth: ClothPatch;
  point: Vector3;
  pointerId: number;
  downX: number;
  downY: number;
}

export class ClothGrabController {
  private cloths: ClothPatch[] = [];
  private active: ActiveGrab | null = null;
  private pending: PendingGrab | null = null;
  private raycaster = new Raycaster();
  private ndc = new Vector2();
  private hit = new Vector3();
  private camNormal = new Vector3();
  private moveHandler: ((e: PointerEvent) => void) | null = null;
  private upHandler: ((e: PointerEvent) => void) | null = null;
  private downHandler: ((e: PointerEvent) => void) | null = null;

  constructor(private deps: ClothGrabDeps) {}

  /** True while a cloth grab is in progress — A1 rail touch-drag defers to this
   *  so a drag that started on cloth never doubles as a rail move. */
  isGrabbing(): boolean {
    return this.active !== null;
  }

  /** Register a cloth for grab interaction. Order doesn't matter — the
   *  raycaster picks the nearest hit across all registered cloths. */
  register(cloth: ClothPatch): void {
    if (this.cloths.indexOf(cloth) < 0) this.cloths.push(cloth);
  }

  unregister(cloth: ClothPatch): void {
    const i = this.cloths.indexOf(cloth);
    if (i >= 0) this.cloths.splice(i, 1);
  }

  attach(): void {
    if (this.downHandler) return;
    this.downHandler = (e: PointerEvent): void => this.handleDown(e);
    // Capture-phase so we get this BEFORE OrbitControls. stopPropagation()
    // on a hit prevents the orbiter from starting a camera drag.
    this.deps.domElement.addEventListener('pointerdown', this.downHandler, { capture: true });
  }

  detach(): void {
    if (this.downHandler) {
      this.deps.domElement.removeEventListener('pointerdown', this.downHandler, { capture: true });
      this.downHandler = null;
    }
    this.endGrab();
    this.clearPending();
  }

  // ── private ────────────────────────────────────────────────────────────

  private setNdc(e: PointerEvent): void {
    const rect = this.deps.domElement.getBoundingClientRect();
    this.ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  }

  private handleDown(e: PointerEvent): void {
    if (this.active || this.pending) return; // already in a gesture
    // Ignore secondary mouse buttons; canvas drag is a left-click affair.
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (this.cloths.length === 0) return;

    this.setNdc(e);
    this.raycaster.setFromCamera(this.ndc, this.deps.camera);

    // Find the nearest cloth hit across all registered cloths. Each cloth's
    // mesh is open / double-sided so we get hits from both faces.
    let nearest: { cloth: ClothPatch; point: Vector3; distance: number } | null = null;
    for (const cloth of this.cloths) {
      const hits = this.raycaster.intersectObject(cloth.mesh, true);
      if (hits.length === 0) continue;
      const h = hits[0];
      if (!nearest || h.distance < nearest.distance) {
        nearest = { cloth: cloth, point: h.point.clone(), distance: h.distance };
      }
    }
    if (!nearest) return;

    // DEFER the grab: record the cloth hit but DON'T grab, stopPropagation, or
    // mute controls yet — a TAP here must click THROUGH to the hero behind (the
    // chair sat behind the draped cloth). The grab commits only once the pointer
    // drags past GRAB_DRAG_THRESHOLD_PX (handleMove). No stopPropagation: the rail
    // stays tap-friendly and yields to us via isGrabbing() the moment we commit.
    this.pending = { cloth: nearest.cloth, point: nearest.point, pointerId: e.pointerId, downX: e.clientX, downY: e.clientY };
    this.addGestureListeners();
  }

  /** window + capture so we see the drag BEFORE the rail's canvas pointermove
   *  (which checks isGrabbing() and bows out once we commit), and so a drag that
   *  leaves the canvas still drives the grab. Added on pointerdown, removed on end. */
  private addGestureListeners(): void {
    if (this.moveHandler) return;
    this.moveHandler = (ev: PointerEvent): void => this.handleMove(ev);
    this.upHandler = (ev: PointerEvent): void => this.handleUp(ev);
    window.addEventListener('pointermove', this.moveHandler, true);
    window.addEventListener('pointerup', this.upHandler, true);
    window.addEventListener('pointercancel', this.upHandler, true);
  }

  private removeGestureListeners(): void {
    if (this.moveHandler) { window.removeEventListener('pointermove', this.moveHandler, true); this.moveHandler = null; }
    if (this.upHandler) {
      window.removeEventListener('pointerup', this.upHandler, true);
      window.removeEventListener('pointercancel', this.upHandler, true);
      this.upHandler = null;
    }
  }

  private handleMove(e: PointerEvent): void {
    // Pending → promote to an active grab once the drag clears the threshold.
    if (this.pending && e.pointerId === this.pending.pointerId) {
      if (Math.hypot(e.clientX - this.pending.downX, e.clientY - this.pending.downY) < GRAB_DRAG_THRESHOLD_PX) return;
      const p = this.pending;
      const particle = p.cloth.grabAt(p.point);
      if (particle < 0) { this.clearPending(); return; }
      // Drag plane: perpendicular to the camera through the hit point, so the
      // grabbed point tracks the cursor in the screen plane.
      this.deps.camera.getWorldDirection(this.camNormal).negate();
      const plane = new Plane().setFromNormalAndCoplanarPoint(this.camNormal, p.point);
      this.active = { cloth: p.cloth, particle, plane, pointerId: p.pointerId };
      this.pending = null;
      if (this.deps.controlsToggle) this.deps.controlsToggle.enabled = false;
      try { this.deps.domElement.setPointerCapture(p.pointerId); } catch { /* focus race — safe to ignore */ }
      // fall through to drag this same move
    }
    if (!this.active || e.pointerId !== this.active.pointerId) return;
    this.setNdc(e);
    this.raycaster.setFromCamera(this.ndc, this.deps.camera);
    if (!this.raycaster.ray.intersectPlane(this.active.plane, this.hit)) return;
    this.active.cloth.moveGrab(this.hit);
  }

  private handleUp(e: PointerEvent): void {
    if (this.active && e.pointerId === this.active.pointerId) { this.endGrab(); return; }
    // A press that never dragged past the threshold — a TAP. Drop it without
    // grabbing; the browser's 'click' then resolves to the hero behind the cloth.
    if (this.pending && e.pointerId === this.pending.pointerId) this.clearPending();
  }

  private clearPending(): void {
    this.pending = null;
    this.removeGestureListeners();
  }

  private endGrab(): void {
    if (!this.active) return;
    this.active.cloth.releaseGrab();
    if (this.deps.controlsToggle) this.deps.controlsToggle.enabled = true;
    try {
      this.deps.domElement.releasePointerCapture(this.active.pointerId);
    } catch {
      /* may have already been released */
    }
    this.active = null;
    this.removeGestureListeners();
  }
}
